import { FoaPart } from "../../../../../autogen/bff-api";

export interface FoaPartBanner {
  messageKey: string;
  status: "info" | "warning";
}

// UX-only: drives the value-only "pending" hint above the national threshold when the exact
// FOA part isn't yet determinable (no contract nature). Authoritative classification is
// server-side in FoaPartClassifier — do NOT replicate the full threshold table here.
export const NATIONAL_THRESHOLD_NOK = 1_300_000;

// Banner copy is consumed via a dynamic t(banner.messageKey) call, so langen can't see it.
// These magic comments register the keys statically — keep them verbatim with the switch below.
// t('This procurement is below the Procurement Act threshold — it should run in the simplified flow')
// t('This is a Del I procurement — it should run in the simplified flow')
// t('FOA Part II — national procurement, announced on Doffin')
// t('FOA Part III — over the EEA threshold, announced on Doffin and TED')
// t('FOA Part IV — special services')
// t('Over the national threshold — a public procurement announced on Doffin. Choose a contract type for the exact FOA part.')
export const foaPartBanner = (
  foaPart: FoaPart | null | undefined,
  estimatedValueNok?: number | null,
): FoaPartBanner | null => {
  if (foaPart != null) {
    switch (foaPart) {
      case FoaPart.OutsideFoa:
        return { messageKey: "This procurement is below the Procurement Act threshold — it should run in the simplified flow", status: "info" };
      case FoaPart.PartI:
        return { messageKey: "This is a Del I procurement — it should run in the simplified flow", status: "info" };
      case FoaPart.PartIi:
        return { messageKey: "FOA Part II — national procurement, announced on Doffin", status: "info" };
      case FoaPart.PartIii:
        return { messageKey: "FOA Part III — over the EEA threshold, announced on Doffin and TED", status: "info" };
      case FoaPart.PartIv:
        return { messageKey: "FOA Part IV — special services", status: "info" };
      default: {
        const _exhaustive: never = foaPart;
        return _exhaustive;
      }
    }
  }
  if (estimatedValueNok != null && estimatedValueNok >= NATIONAL_THRESHOLD_NOK) {
    return {
      messageKey:
        "Over the national threshold — a public procurement announced on Doffin. Choose a contract type for the exact FOA part.",
      status: "info",
    };
  }
  return null;
};

// Coarse pre-creation routing. The fork is the single national threshold (1.3M), already
// declared above for the in-wizard banner. The exact FOA part (Part II/III/IV) is NOT computed
// here — it stays server-derived once the event exists (see FoaPartClassifier).
export const routesToSimplifiedFlow = (estimatedValueNok: number | null | undefined): boolean =>
  estimatedValueNok != null && estimatedValueNok < NATIONAL_THRESHOLD_NOK;

export interface ModalRoutingBanner {
  kind: "simplified" | "public";
  messageKey: string;
  status: "info" | "warning";
}

// Coarse routing copy: the modal forks on the single national threshold and does NOT compute the
// exact FOA part, so the simplified message stays part-agnostic — claiming "Del I" here would be
// wrong for below-500K drafts, which FoaPartClassifier classifies as OutsideFoa, not Part I.
// t('This procurement runs in the simplified flow')
// t('This is a public procurement, announced on Doffin (and TED above the EEA threshold)')
export const modalRoutingBanner = (estimatedValueNok: number | null | undefined): ModalRoutingBanner | null => {
  if (estimatedValueNok == null) return null;
  return routesToSimplifiedFlow(estimatedValueNok)
    ? {
        kind: "simplified",
        messageKey: "This procurement runs in the simplified flow",
        status: "info",
      }
    : {
        kind: "public",
        messageKey: "This is a public procurement, announced on Doffin (and TED above the EEA threshold)",
        status: "info",
      };
};
